import type { Rarity } from './rarity';
import { MAX_CARD_LEVEL } from './card-customization';

export const ANIME_RANKS = ['F', 'E', 'D', 'C', 'B', 'A', 'S', 'SS', 'SSS', 'EX', 'DEUS-EX'] as const;
export type AnimeRank = (typeof ANIME_RANKS)[number];

export const BOOSTER_TYPES = ['free', 'standard', 'premium', 'legendary'] as const;
export type BoosterType = (typeof BOOSTER_TYPES)[number];

export type UnlockKind = 'title' | 'badge' | 'frame' | 'avatar' | 'banner';

export type QuestPeriod = 'daily' | 'weekly' | 'monthly';

export const QUEST_EVENTS = [
  'booster_opened',
  'character_new',
  'character_duplicate',
  'collection_viewed',
  'auction_bid',
  'free_booster_opened',
  'premium_booster_opened',
  'legendary_booster_opened',
  'rare_or_better_obtained',
  'epic_or_better_obtained',
  'card_upgraded',
  'fragments_spent',
  'fragments_earned',
  'listing_created',
  'market_purchase',
  'trade_proposed',
  'trade_completed',
  'system_sale',
  'anima_spent',
  'message_sent',
  'card_shared',
  'reaction_given',
  'friend_request_sent',
  'profile_viewed',
  'collection_compared',
  'showcase_edited',
  'favorite_added',
  'cosmetic_equipped',
  'login_claimed',
  'anime_completed',
  'achievement_unlocked',
  'quest_claimed',
] as const;
export type QuestEvent = (typeof QUEST_EVENTS)[number];

export interface RewardUnlock {
  kind: UnlockKind;
  key: string;
  label: string;
}

export interface Reward {
  anima?: number;
  xp?: number;
  
  fragments?: number;
  
  fragmentsChoice?: boolean;
  
  boosters?: Partial<Record<Extract<BoosterType, 'standard' | 'premium' | 'legendary'>, number>>;
  
  characterRarity?: Rarity;
  unlocks?: RewardUnlock[];
}

export interface RarityConfig {
  label: string;
  
  minFavourites: number;
  fragmentsPerDuplicate: number;
  
  baseValue: number;
}

export interface AnimeRankConfig {
  rank: AnimeRank;
  
  minFavourites: number;
}

export interface AnimeCompletionConfig {
  
  animaPerCharacter: number;
  
  boosterPerCharacters: Partial<Record<Extract<BoosterType, 'standard' | 'premium' | 'legendary'>, number>>;
}

export interface PityRule {
  
  minRarity: Rarity;
  after: number;
}

export interface BoosterConfig {
  id: BoosterType;
  name: string;
  description: string;
  price: number;
  cardsPerOpening: number;
  
  rates: Partial<Record<Rarity, number>>;
  
  minRarity: Rarity;
  pity: PityRule[];
  xpPerOpening: number;
  enabled: boolean;
  
  featuredAnimeIds?: number[];
  unavailableReason?: string;
}

export interface UpgradeLevelConfig {
  level: number;
  
  totalCost: number;
  unlock: string;
}

export interface ProgressionConfig {
  
  xpBase: number;
  xpQuadratic: number;
  maxLevel: number;
  
  levelRewardAnimaPerLevel: number;
}

export interface FreeBoosterConfig {
  
  baseMax: number;
  
  perLevel: number;
  
  fullRechargeMs: number;
}

export const VISIBILITY_LEVELS = ['public', 'friends', 'private'] as const;
export type VisibilityLevel = (typeof VISIBILITY_LEVELS)[number];

export const MESSAGE_REACTIONS = ['heart', 'fire', 'star', 'spark', 'laugh', 'wow'] as const;
export type MessageReaction = (typeof MESSAGE_REACTIONS)[number];

export const REPORT_REASONS = ['spam', 'harassment', 'inappropriate', 'impersonation', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export interface SocialConfig {
  maxFriends: number;
  maxPendingRequests: number;
  declineCooldownMs: number;
  showcaseSlots: number;
  maxMessageLength: number;
  reactions: MessageReaction[];
  onlineAfterMs: number;
  awayAfterMs: number;
  notificationRetentionDays: number;
  unreadNotificationRetentionDays: number;
  activityRetentionDays: number;
  deletedMessageRetentionDays: number;
}

export const AUCTION_DURATIONS_HOURS = [1, 6, 12, 24] as const;
export type AuctionDurationHours = (typeof AUCTION_DURATIONS_HOURS)[number];

export interface MarketConfig {
  minLevel: number;
  saleFeePercent: number;
  
  systemBuybackPercent: number;
  
  systemBuybackPotPercent: number;
  minPrice: number;
  
  maxPriceValueMultiplier: number;
  
  lowPriceFlagRatio: number;
  
  highPriceFlagMultiplier: number;
  listingDurationMs: number;
  maxActiveListings: number;
  maxListingsPerDay: number;
  maxActiveAuctions: number;
  maxAuctionsPerDay: number;
  auctionDurationsHours: readonly AuctionDurationHours[];
  
  auctionIncrementBase: number;
  
  auctionIncrementMultiplier: number;
  
  auctionMaxBidMultiplier: number;
  antiSnipeMs: number;
  maxTradeCardsPerSide: number;
  maxTradeAnima: number;
  maxPendingTrades: number;
  tradeMinDurationMs: number;
  tradeMaxDurationMs: number;
  
  repeatPairFlagCount: number;
  repeatPairWindowMs: number;
}


export interface CombatConfig {
  
  rarityBase: Record<Rarity, number>;
  
  fameWeight: number;
  fameHalf: number;
  
  hypeWeight: number;
  hypeHalf: number;
  
  levelStep: number;
  
  nameLean: number;
  
  nameLeanScale: number;
  
  nameLeanOffset: number;
  
  jitter: number;
  minAttackShare: number;
  maxAttackShare: number;
  
  roleModifiers: Record<'attacker' | 'tank' | 'support', { attack: number; defense: number }>;
}

export interface GameConfig {
  startingAnima: number;
  rarities: Record<Rarity, RarityConfig>;
  animeRanks: AnimeRankConfig[];
  
  animeCompletion: AnimeCompletionConfig;
  boosters: BoosterConfig[];
  
  duplicatePullChance: number;
  allowedQuantities: number[];
  upgrades: UpgradeLevelConfig[];
  progression: ProgressionConfig;
  freeBooster: FreeBoosterConfig;
  social: SocialConfig;
  market: MarketConfig;
  combat: CombatConfig;
}


export const GAME_CONFIG: GameConfig = {
  startingAnima: 1000,
  rarities: {
    common: { label: 'Commun', minFavourites: 0, fragmentsPerDuplicate: 5, baseValue: 10 },
    uncommon: { label: 'Peu-Commun', minFavourites: 50, fragmentsPerDuplicate: 8, baseValue: 25 },
    rare: { label: 'Rare', minFavourites: 200, fragmentsPerDuplicate: 15, baseValue: 75 },
    epic: { label: 'Épique', minFavourites: 750, fragmentsPerDuplicate: 25, baseValue: 200 },
    legendary: { label: 'Légendaire', minFavourites: 3000, fragmentsPerDuplicate: 50, baseValue: 600 },
    mythic: { label: 'Mythique', minFavourites: 6000, fragmentsPerDuplicate: 100, baseValue: 2000 },
    divine: { label: 'Divine', minFavourites: 14000, fragmentsPerDuplicate: 200, baseValue: 6000 },
    // Jamais attribuée par les favoris : seul l'owner la donne, depuis le panel.
    senita: { label: 'Senita', minFavourites: Number.POSITIVE_INFINITY, fragmentsPerDuplicate: 1000, baseValue: 60000 },
  },
  animeRanks: [
    { rank: 'F', minFavourites: 0 },
    { rank: 'E', minFavourites: 10 },
    { rank: 'D', minFavourites: 30 },
    { rank: 'C', minFavourites: 60 },
    { rank: 'B', minFavourites: 100 },
    { rank: 'A', minFavourites: 160 },
    { rank: 'S', minFavourites: 250 },
    { rank: 'SS', minFavourites: 400 },
    { rank: 'SSS', minFavourites: 600 },
    { rank: 'EX', minFavourites: 850 },
    { rank: 'DEUS-EX', minFavourites: 1200 },
  ],
  
  animeCompletion: {
    animaPerCharacter: 400,
    boosterPerCharacters: { standard: 5, premium: 10, legendary: 20 },
  },
  boosters: [
    {
      id: 'free',
      name: 'Booster Gratuit',
      description: 'Toutes les raretés, avec une chance très faible au-dessus d’Épique. Réserve qui se recharge en une heure.',
      price: 0,
      cardsPerOpening: 1,
      minRarity: 'common',
      rates: { common: 81.9239, uncommon: 14, rare: 3.5, epic: 0.5, legendary: 0.07, mythic: 0.005, divine: 0.001, senita: 0.0001 },
      pity: [
        { minRarity: 'divine', after: 6000 },
        { minRarity: 'mythic', after: 1800 },
        { minRarity: 'legendary', after: 900 },
        { minRarity: 'epic', after: 300 },
        { minRarity: 'rare', after: 60 },
      ],
      xpPerOpening: 5,
      enabled: true,
    },
    {
      id: 'standard',
      name: 'Booster Standard',
      description: 'Toutes les raretés, avec de meilleures chances de tomber sur du Peu-Commun et du Rare.',
      price: 145,
      cardsPerOpening: 1,
      minRarity: 'common',
      rates: { common: 58.7878, uncommon: 30, rare: 9, epic: 1.8, legendary: 0.35, mythic: 0.06, divine: 0.002, senita: 0.0002 },
      pity: [
        { minRarity: 'divine', after: 4000 },
        { minRarity: 'mythic', after: 1200 },
        { minRarity: 'legendary', after: 400 },
        { minRarity: 'epic', after: 200 },
        { minRarity: 'rare', after: 40 },
      ],
      xpPerOpening: 10,
      enabled: true,
    },
    {
      id: 'premium',
      name: 'Booster Premium',
      description: 'Toutes les raretés, avec une courbe centrée sur Rare et de bonnes chances d’Épique.',
      price: 375,
      cardsPerOpening: 1,
      minRarity: 'common',
      rates: {
        uncommon: 55,
        rare: 32,
        epic: 10,
        legendary: 2.744,
        mythic: 0.25,
        divine: 0.005,
        senita: 0.0005
      },
      pity: [
        { minRarity: 'divine', after: 1600 },
        { minRarity: 'mythic', after: 480 },
        { minRarity: 'legendary', after: 160 },
        { minRarity: 'epic', after: 80 },
        { minRarity: 'rare', after: 16 },
      ],
      xpPerOpening: 25,
      enabled: true,
    },
    {
      id: 'legendary',
      name: 'Booster Chanceux',
      description: 'Toutes les raretés, avec une courbe très favorable aux raretés élevées.',
      price: 860,
      cardsPerOpening: 1,
      minRarity: 'common',
      rates: {
        rare: 55,
        epic: 32,
        legendary: 12.178,
        mythic: 0.8,
        divine: 0.02,
        senita: 0.002
      },
      pity: [
        { minRarity: 'divine', after: 500 },
        { minRarity: 'mythic', after: 150 },
        { minRarity: 'legendary', after: 50 },
        { minRarity: 'epic', after: 26 },
        { minRarity: 'rare', after: 6 },
      ],
      xpPerOpening: 60,
      enabled: true,
    },
  ],
  duplicatePullChance: 0.05,
  // Toute quantité de 1 à 10 ; l’UI propose « 1 » ou « Max » (plafond 10).
  allowedQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
  upgrades: [
    { level: 1, totalCost: 0, unlock: 'Carte de base' },
    { level: 2, totalCost: 50, unlock: 'Surnom personnalisé' },
    { level: 3, totalCost: 150, unlock: 'Couleur du nom' },
    { level: 4, totalCost: 350, unlock: 'Bordure personnalisée' },
    { level: 5, totalCost: 750, unlock: 'Emblème de carte' },
    { level: 6, totalCost: 1350, unlock: 'Halo coloré' },
    { level: 7, totalCost: 2200, unlock: 'Finition holographique' },
    { level: 8, totalCost: 3350, unlock: 'Signature personnelle' },
    { level: 9, totalCost: 4850, unlock: 'Aura animée' },
    { level: MAX_CARD_LEVEL, totalCost: 6750, unlock: 'Portrait éveillé' },
  ],
  progression: {
    // Level 1 → 2: 100 EXP · 10 → 11: 1,135 · 50 → 51: 8,675 · 85 → 86: 19,210 · 100 → 101: 24,850.
    // Must stay in sync with migrations/0039_steeper_xp_curve.sql.
    xpBase: 100,
    xpQuadratic: 1.5,
    maxLevel: 250,
    levelRewardAnimaPerLevel: 150,
  },
  freeBooster: {
    baseMax: 10,
    perLevel: 1,
    fullRechargeMs: 60 * 60 * 1000,
  },
  social: {
    maxFriends: 200,
    maxPendingRequests: 50,
    declineCooldownMs: 72 * 60 * 60 * 1000,
    showcaseSlots: 6,
    maxMessageLength: 1000,
    reactions: [...MESSAGE_REACTIONS],
    onlineAfterMs: 5 * 60 * 1000,
    awayAfterMs: 30 * 60 * 1000,
    notificationRetentionDays: 30,
    unreadNotificationRetentionDays: 90,
    activityRetentionDays: 90,
    deletedMessageRetentionDays: 30,
  },
  market: {
    minLevel: 3,
    saleFeePercent: 5,
    systemBuybackPercent: 50,
    systemBuybackPotPercent: 5,
    minPrice: 10,
    maxPriceValueMultiplier: 50,
    lowPriceFlagRatio: 0.25,
    highPriceFlagMultiplier: 20,
    listingDurationMs: 14 * 24 * 60 * 60 * 1000,
    maxActiveListings: 20,
    maxListingsPerDay: 30,
    maxActiveAuctions: 10,
    maxAuctionsPerDay: 15,
    auctionDurationsHours: AUCTION_DURATIONS_HOURS,
    auctionIncrementBase: 2,
    auctionIncrementMultiplier: 2,
    auctionMaxBidMultiplier: 1.2,
    antiSnipeMs: 2 * 60 * 1000,
    maxTradeCardsPerSide: 15,
    maxTradeAnima: 20_000,
    maxPendingTrades: 10,
    tradeMinDurationMs: 24 * 60 * 60 * 1000,
    tradeMaxDurationMs: 7 * 24 * 60 * 60 * 1000,
    repeatPairFlagCount: 5,
    repeatPairWindowMs: 7 * 24 * 60 * 60 * 1000,
  },
  combat: {
    rarityBase: { common: 100, uncommon: 140, rare: 200, epic: 280, legendary: 400, mythic: 560, divine: 780, senita: 1100 },
    // Both bonuses together stay under x1.4, so at equal level a higher rarity always has the bigger total.
    fameWeight: 0.2,
    fameHalf: 1000,
    hypeWeight: 0.1,
    hypeHalf: 200_000,
    levelStep: 0.15,
    nameLean: 0.15,
    nameLeanScale: 4,
    nameLeanOffset: 3.5,
    jitter: 0.2,
    minAttackShare: 0.25,
    maxAttackShare: 0.75,
    // Attacker and tank move 30% of the stats from one side to the other; support keeps them as they are.
    roleModifiers: {
      attacker: { attack: 1.3, defense: 0.7 },
      tank: { attack: 0.7, defense: 1.3 },
      support: { attack: 1, defense: 1 },
    },
  },
};
